/**
 * Match chronicle: the story of a match for the results screen — a short, bounded list of the
 * moments that mattered (alliances made and betrayed, ceasefires, big gifts, the first launch,
 * strikes that cost a nation cities / its capital / a slice of its people, capitals going
 * critical and recovering, who knocked out whom, lead changes, a domination threat, the final
 * minute, the end) plus a few per-nation strike tallies the sim's stats do not keep.
 *
 * Built next to the sim, never inside it: `Chronicler.observe(state, events)` after every
 * `step` (server MatchRunner, offline worker) reads the SimEvents and the state read-only, so
 * the determinism hash is untouched. The match is over when it is sent, so the chronicle tells
 * everything (no fog) — and it is the same record for every player.
 */
import { BALANCE, TICK_HZ, allied, landHexAt, type EndReason, type MatchState, type PlayerId, type SimEvent, type Tick } from '@sa/sim';

export type ChronicleKind =
  | 'ally'
  | 'betrayal'
  | 'ceasefire'
  | 'truceBroken'
  | 'gift'
  | 'firstLaunch'
  | 'strike'
  | 'critical'
  | 'recovered'
  | 'out'
  | 'leader'
  | 'domination'
  | 'final'
  | 'end';

/**
 * One moment. `a` is the nation the moment is about (its colour on the timeline), `b` the other
 * one (0: none / unknown):
 *   ally / ceasefire: a < b, the pair (a renewed ceasefire is not told again) · betrayal / truceBroken: a broke it with b
 *   gift: a sent b `n` credits (gifts between the pair within a minute merged)
 *   firstLaunch: a fired the match's first warheads, `n` of them in the opening 10 s
 *   strike: a's warheads landed on b's land (a salvo / bomber run merged): `n` warheads, `c` cities
 *     destroyed, `p` % of b's people killed, `f` & STRIKE_CAPITAL: b's capital lost a level (or went critical)
 *   critical: a's capital went critical, b did it · recovered: a's capital recovered
 *   out: a was eliminated, by b; `f`: OUT_* flags
 *   leader: a took the lead (b: from whom, 0 the first leader), `n` = a's score
 *   domination: a holds `n` % of the land (domination threat; `f` 1: a's team)
 *   final: the final minute — a leads b by `n` points · end: a won (first winner, 0 nobody), `n` winners, `f` = END_REASONS index
 */
export interface ChronicleEntry {
  t: Tick;
  k: ChronicleKind;
  a: PlayerId;
  b: PlayerId;
  n?: number;
  c?: number;
  p?: number;
  f?: number;
}

/** who knocked whom out, per match state (written by `Chronicler.out`, read by `killerOf`) */
const KILLERS = new WeakMap<MatchState, Map<PlayerId, PlayerId>>();

/**
 * Who knocked `v` out, as the chronicle tells it (0: nobody / not known / no chronicler ran): the
 * one source for the killer the `eliminated` phase event carries (`eliminatedPhaseB`), so the
 * toast, the knock-out panel and the results never disagree.
 */
export function killerOf(state: MatchState, v: PlayerId): PlayerId {
  return KILLERS.get(state)?.get(v) ?? 0;
}

/** The `eliminated` phase event's `b`: bit 0 decapitated, the killer above it (`b >> 1`, 0: none). */
export const eliminatedPhaseB = (decapitated: boolean, by: PlayerId): number => (by << 1) | (decapitated ? 1 : 0);
/** …and back: `{ decapitated, by }` from an `eliminated` phase event's `b`. */
export const eliminatedFromB = (b: number): { decapitated: boolean; by: PlayerId } => ({ decapitated: (b & 1) === 1, by: b >> 1 });

/** Per-nation strike numbers the sim's MatchStats do not separate. */
export interface ChronicleTally {
  id: PlayerId;
  /** own warheads (ICBM / SLBM / bomb) that went off on a living rival's land */
  onTarget: number;
  /** own warheads that went off anywhere else: neutral land (a fallen nation's too), sea, own / allied land */
  wasted: number;
  /** rival cities own warheads destroyed */
  citiesDestroyed: number;
  /** hostile ICBMs / SLBMs own SAMs / ships / fighters shot down */
  shotDown: number;
  /** credits given to other nations */
  gifted: number;
}

export interface Chronicle {
  /** chronological, at most CHRONICLE_MAX */
  entries: ChronicleEntry[];
  tallies: ChronicleTally[];
}

export const CHRONICLE_MAX = 40;
export const STRIKE_CAPITAL = 1;
/** out: the capital was ruined (decapitation) / captured / the nation surrendered */
export const OUT_DECAP = 1;
export const OUT_TAKEN = 2;
export const OUT_SURRENDER = 4;
export const END_REASONS: readonly EndReason[] = ['elimination', 'time', 'domination', 'surrender'];

const S = TICK_HZ;
/** detonations of one nation on another this close together are one strike */
const STRIKE_GAP = 30 * S;
/** gifts between a pair this close together are one gift; smaller totals are not told */
const GIFT_GAP = 60 * S;
const GIFT_MIN = 1_000;
/** the opening salvo: launches this soon after the first one count with it */
const FIRST_LAUNCH_WINDOW = 10 * S;
/**
 * a strike is told when it cost the victim's capital a level, destroyed this many cities, killed this
 * share of the victim's people or was this big (bots trade one-warhead city hits all match long)
 */
const STRIKE_CITIES = 2;
const STRIKE_POP_PCT = 8;
const STRIKE_BIG = 4;
/** leads are told from this tick, once held this long */
const LEAD_FROM = BALANCE.phases.releaseTick;
const LEAD_HOLD = 30 * S;
const LEAD_EVERY = S;
/** a domination streak is told once it has held this long */
const DOMINATION_WARN = 15 * S;
const FINAL_MINUTE = 60 * S;
/** land taken from a nation: sampled this often, decayed per sample (a minute or so of memory) */
const LAND_EVERY = 2 * S;
const LAND_DECAY = 0.93;
/** a knock-out / critical is pinned on the last striker of this long ago at most */
const BLOW_MEMORY = 60 * S;
/** the raw list is pruned to CHRONICLE_MAX · 2 when it passes this (very long / chaotic matches) */
const RAW_MAX = 200;

/** How much a moment matters: picks the key moments and decides what a full chronicle drops. */
export function chronicleWeight(e: ChronicleEntry): number {
  switch (e.k) {
    case 'end':
      return 100;
    case 'out':
      return 90 + ((e.f ?? 0) & (OUT_DECAP | OUT_TAKEN) ? 5 : 0);
    case 'betrayal':
      return 85;
    case 'domination':
      return 80;
    case 'critical':
      return 70;
    case 'truceBroken':
      return 60;
    case 'final':
      return 55;
    case 'strike':
      return Math.min(78, 30 + 8 * (e.c ?? 0) + ((e.f ?? 0) & STRIKE_CAPITAL ? 20 : 0) + Math.min(15, (e.p ?? 0) / 2) + Math.min(8, e.n ?? 0));
    case 'ally':
      return 50;
    case 'recovered':
      return 45;
    case 'leader':
      return 40;
    case 'firstLaunch':
      return 38;
    case 'ceasefire':
      return 35;
    case 'gift':
      return 20 + Math.min(20, (e.n ?? 0) / 250);
  }
}

/** Keep the `max` weightiest entries (ties: the earlier), in time order. */
export function boundChronicle(entries: readonly ChronicleEntry[], max = CHRONICLE_MAX): ChronicleEntry[] {
  const ordered = [...entries].sort((x, y) => x.t - y.t);
  if (ordered.length <= max) return ordered;
  const keep = new Set(
    ordered
      .map((e, i) => ({ e, i, w: chronicleWeight(e) }))
      .sort((x, y) => y.w - x.w || x.i - y.i)
      .slice(0, max)
      .map((x) => x.e),
  );
  return ordered.filter((e) => keep.has(e));
}

interface Strike {
  a: PlayerId;
  b: PlayerId;
  t0: Tick;
  last: Tick;
  n: number;
  c: number;
  killed: number;
  /** the victim's people when the strike began */
  pop0: number;
  capital: boolean;
}

interface Gift {
  a: PlayerId;
  b: PlayerId;
  t0: Tick;
  last: Tick;
  n: number;
}

/** Builds the chronicle from every step's events; see the file comment. */
export class Chronicler {
  private entries: ChronicleEntry[] = [];
  private readonly tallies: ChronicleTally[];
  private readonly np: number;
  /** owner per land hex at the end of the previous step (impacts are judged on the land as it was) */
  private readonly prevOwner: Uint8Array;
  /** land sample for "who took their land": `taken[v * (np + 1) + w]` decays per sample */
  private readonly landSample: Uint8Array;
  private readonly taken: Float64Array;
  private prevPop: number[];
  private prevCitiesLost: number[];
  private prevPopKilled: number[];
  private prevCriticals: number[];
  private prevCapLvl: number[];
  private readonly lastBlow: ({ by: PlayerId; t: Tick } | null)[];
  private strikes = new Map<number, Strike>();
  private gifts = new Map<number, Gift>();
  private first: ChronicleEntry | null = null;
  private leader = 0;
  private candidate = 0;
  private candidateSince = 0;
  private dominationTold = -1;
  /** ceasefire pairs already told (a renewal is not news; one after a break is) */
  private readonly truces = new Set<number>();
  /** nations whose fresh critical was told this step */
  private readonly critSeen = new Set<PlayerId>();

  constructor(state: MatchState) {
    this.np = state.players.length;
    this.tallies = state.players.map((p) => ({ id: p.id, onTarget: 0, wasted: 0, citiesDestroyed: 0, shotDown: 0, gifted: 0 }));
    this.prevOwner = Uint8Array.from(state.owner.subarray(0, state.world.landCount));
    this.landSample = Uint8Array.from(this.prevOwner);
    this.taken = new Float64Array((this.np + 1) * (this.np + 1));
    this.prevPop = state.players.map((p) => p.pop);
    this.prevCitiesLost = state.stats.players.map((s) => s.citiesLost);
    this.prevPopKilled = state.stats.players.map((s) => s.popKilled);
    this.prevCriticals = state.stats.players.map((s) => s.capitalCriticals);
    this.prevCapLvl = state.players.map((p) => state.cityLvl[p.capitalHex]);
    this.lastBlow = state.players.map(() => null);
  }

  /** After each `step(state, …)`: read its events (and the state, read-only). */
  observe(state: MatchState, events: readonly SimEvent[]): void {
    const t = state.tick;
    // this step's impacts per (shooter, victim); victim 0 = open ground
    const hits = new Map<number, number>();
    const pair = (a: number, b: number) => a * 16 + b;
    // interceptor owner per target shot down this step, and the warheads lost to interception
    const downBy = new Map<number, PlayerId>();
    const downed: { id: number; owner: PlayerId }[] = [];
    this.critSeen.clear();
    for (const ev of events) {
      switch (ev.type) {
        case 'launch':
          this.launch(ev.owner, t);
          break;
        case 'detonation': {
          if (ev.owner <= 0) break;
          const h = landHexAt(state.geo, ev.x, ev.y);
          const v = h >= 0 ? this.prevOwner[h] : 0;
          const rival = v > 0 && v !== ev.owner && !allied(state, ev.owner, v);
          const tally = this.tallies[ev.owner - 1];
          if (rival) {
            tally.onTarget++;
            this.lastBlow[v - 1] = { by: ev.owner, t };
          } else tally.wasted++;
          const k = pair(ev.owner, rival ? v : 0);
          hits.set(k, (hits.get(k) ?? 0) + 1);
          break;
        }
        case 'interceptResolve':
          if (ev.hit) downBy.set(ev.targetId, ev.shooterOwner);
          break;
        case 'removed':
          if (ev.reason === 'intercepted' && (ev.kind === 'icbm' || ev.kind === 'slbm')) downed.push({ id: ev.id, owner: ev.owner });
          break;
        case 'gift':
          this.gift(ev.from, ev.to, ev.amount, t);
          break;
        case 'phase':
          this.phase(state, ev.phase.kind, ev.phase.a, ev.phase.b, t, hits);
          break;
        case 'eliminated':
          this.out(state, ev.player, ev.surrendered, t);
          break;
        case 'end': {
          const r = ev.result;
          this.flushAll();
          // a lead taken in the last seconds held to the end: told, though shorter than LEAD_HOLD
          const w = r.winners.length === 1 ? r.winners[0] : 0;
          if (w && w !== this.leader && w === this.candidate) this.push({ t: this.candidateSince, k: 'leader', a: w, b: this.leader, n: state.players[w - 1].score });
          this.push({ t, k: 'end', a: r.winners[0] ?? 0, b: 0, n: r.winners.length, f: END_REASONS.indexOf(r.reason) });
          break;
        }
        default:
          break;
      }
    }
    for (const d of downed) {
      const by = downBy.get(d.id);
      if (by && by !== d.owner) this.tallies[by - 1].shotDown++;
    }
    if (hits.size) this.strikeStep(state, t, hits);
    this.flushStale(t);
    if (t % LAND_EVERY === 0) this.sampleLand(state);
    if (t % LEAD_EVERY === 0 && !state.result) this.lead(state, t);
    this.dominationStep(state, t);
    if (t === state.matchTicks - FINAL_MINUTE && !state.result) this.finalMinute(state, t);
    if (this.entries.length > RAW_MAX) this.entries = boundChronicle(this.entries, CHRONICLE_MAX * 2);
    this.remember(state);
  }

  /** The chronicle so far (complete once the match has ended): bounded, in time order. */
  finish(): Chronicle {
    this.flushAll();
    return { entries: boundChronicle(this.entries), tallies: this.tallies.map((x) => ({ ...x })) };
  }

  // -------------------------------------------------------------------------

  private push(e: ChronicleEntry): void {
    this.entries.push(e);
  }

  private remember(state: MatchState): void {
    this.prevOwner.set(state.owner.subarray(0, this.prevOwner.length));
    for (const p of state.players) {
      const i = p.id - 1;
      const st = state.stats.players[i];
      this.prevPop[i] = p.pop;
      this.prevCitiesLost[i] = st.citiesLost;
      this.prevPopKilled[i] = st.popKilled;
      this.prevCriticals[i] = st.capitalCriticals;
      this.prevCapLvl[i] = state.cityLvl[p.capitalHex];
    }
  }

  private launch(owner: PlayerId, t: Tick): void {
    if (!this.first) {
      this.first = { t, k: 'firstLaunch', a: owner, b: 0, n: 1 };
      this.push(this.first);
    } else if (this.first.a === owner && t - this.first.t <= FIRST_LAUNCH_WINDOW) this.first.n = (this.first.n ?? 0) + 1;
  }

  private gift(from: PlayerId, to: PlayerId, amount: number, t: Tick): void {
    this.tallies[from - 1].gifted += amount;
    const k = from * 16 + to;
    const g = this.gifts.get(k);
    if (g && t - g.last <= GIFT_GAP) {
      g.n += amount;
      g.last = t;
    } else {
      if (g) this.flushGift(g);
      this.gifts.set(k, { a: from, b: to, t0: t, last: t, n: amount });
    }
  }

  /**
   * This step's impacts on rivals become (or extend) strikes. The sim keeps cities lost per
   * victim and pop killed per shooter: a victim's cities go to whoever hit it most this step, a
   * shooter's kills are split over its victims by impacts.
   */
  private strikeStep(state: MatchState, t: Tick, hits: Map<number, number>): void {
    const byShooter = new Map<PlayerId, number>();
    const topOn = new Map<PlayerId, { a: PlayerId; n: number }>();
    for (const [k, n] of hits) {
      const a = k >> 4;
      const b = k & 15;
      if (!b) continue;
      byShooter.set(a, (byShooter.get(a) ?? 0) + n);
      const top = topOn.get(b);
      if (!top || n > top.n) topOn.set(b, { a, n });
    }
    for (const [k, n] of hits) {
      const a = k >> 4;
      const b = k & 15;
      if (!b) continue;
      const killed = ((state.stats.players[a - 1].popKilled - this.prevPopKilled[a - 1]) * n) / byShooter.get(a)!;
      const cities = topOn.get(b)!.a === a ? Math.max(0, state.stats.players[b - 1].citiesLost - this.prevCitiesLost[b - 1]) : 0;
      const victim = state.players[b - 1];
      const capital = state.cityLvl[victim.capitalHex] < this.prevCapLvl[b - 1] || state.stats.players[b - 1].capitalCriticals > this.prevCriticals[b - 1];
      this.tallies[a - 1].citiesDestroyed += cities;
      let s = this.strikes.get(k);
      if (s && t - s.last > STRIKE_GAP) {
        this.flushStrike(s);
        s = undefined;
      }
      if (!s) {
        s = { a, b, t0: t, last: t, n: 0, c: 0, killed: 0, pop0: this.prevPop[b - 1], capital: false };
        this.strikes.set(k, s);
      }
      s.last = t;
      s.n += n;
      s.c += cities;
      s.killed += killed;
      s.capital ||= capital;
    }
  }

  private flushStale(t: Tick): void {
    for (const s of this.strikes.values()) if (t - s.last > STRIKE_GAP) this.flushStrike(s);
    for (const g of this.gifts.values()) if (t - g.last > GIFT_GAP) this.flushGift(g);
  }

  private flushAll(): void {
    for (const s of [...this.strikes.values()]) this.flushStrike(s);
    for (const g of [...this.gifts.values()]) this.flushGift(g);
  }

  private flushStrike(s: Strike): void {
    this.strikes.delete(s.a * 16 + s.b);
    const p = s.pop0 > 0 ? Math.min(100, Math.round((100 * s.killed) / s.pop0)) : 0;
    if (!(s.c >= STRIKE_CITIES || s.capital || p >= STRIKE_POP_PCT || s.n >= STRIKE_BIG)) return;
    this.push({ t: s.t0, k: 'strike', a: s.a, b: s.b, n: s.n, ...(s.c ? { c: s.c } : {}), ...(p ? { p } : {}), ...(s.capital ? { f: STRIKE_CAPITAL } : {}) });
  }

  private flushGift(g: Gift): void {
    this.gifts.delete(g.a * 16 + g.b);
    if (g.n >= GIFT_MIN) this.push({ t: g.t0, k: 'gift', a: g.a, b: g.b, n: g.n });
  }

  private phase(state: MatchState, kind: string, a: number, b: number, t: Tick, hits: Map<number, number>): void {
    switch (kind) {
      case 'ally':
        this.push({ t, k: 'ally', a, b });
        break;
      case 'allyBroken':
        // an eliminated nation's alliance dissolves with it: not a betrayal
        if (state.players[a - 1]?.eliminatedTick !== t) this.push({ t, k: 'betrayal', a, b });
        break;
      case 'ceasefire':
        if (this.truces.has(a * 16 + b)) break;
        this.truces.add(a * 16 + b);
        this.push({ t, k: 'ceasefire', a, b });
        break;
      case 'ceasefireBroken':
        this.truces.delete(Math.min(a, b) * 16 + Math.max(a, b));
        this.push({ t, k: 'truceBroken', a, b });
        break;
      case 'capitalCritical': {
        // the event repeats while critical (each hit restarts the window): only a fresh one is told
        const st = state.stats.players[a - 1];
        if (!st || st.capitalCriticals <= this.prevCriticals[a - 1] || this.critSeen.has(a)) break;
        this.critSeen.add(a);
        this.push({ t, k: 'critical', a, b: this.culprit(state, a, t, hits) });
        break;
      }
      case 'capitalRecovered':
        this.push({ t, k: 'recovered', a, b: 0 });
        break;
      default:
        break;
    }
  }

  /** Who did it to `v` this step: its top striker now, else the creep at its capital, else its last striker. */
  private culprit(state: MatchState, v: PlayerId, t: Tick, hits: Map<number, number>): PlayerId {
    let best = 0;
    let most = 0;
    for (const [k, n] of hits) {
      if ((k & 15) === v && n > most) {
        most = n;
        best = k >> 4;
      }
    }
    if (best) return best;
    const creep = state.progBy[state.players[v - 1].capitalHex];
    if (creep > 0 && creep !== v) return creep;
    const blow = this.lastBlow[v - 1];
    return blow && t - blow.t <= BLOW_MEMORY ? blow.by : 0;
  }

  private out(state: MatchState, v: PlayerId, surrendered: boolean, t: Tick): void {
    const p = state.players[v - 1];
    const blow = this.lastBlow[v - 1];
    const striker = blow && t - blow.t <= BLOW_MEMORY ? blow.by : 0;
    let by = 0;
    let f = 0;
    if (surrendered) f = OUT_SURRENDER;
    else if (state.stats.players[v - 1].decapitated) {
      const captor = state.owner[p.capitalHex];
      if (captor > 0 && captor !== v) {
        by = captor;
        f = OUT_TAKEN;
      } else {
        by = striker;
        f = OUT_DECAP;
      }
    } else by = this.topTaker(v) || striker;
    // its strikes in progress end with it
    for (const s of [...this.strikes.values()]) if (s.b === v) this.flushStrike(s);
    this.push({ t, k: 'out', a: v, b: by, ...(f ? { f } : {}) });
    // (the snapshots of this very step carry it: observe runs before the filter)
    let killers = KILLERS.get(state);
    if (!killers) KILLERS.set(state, (killers = new Map()));
    killers.set(v, by);
  }

  /** The nation that took the most of `v`'s land lately (0: nobody). */
  private topTaker(v: PlayerId): PlayerId {
    let best = 0;
    let most = 0.5;
    for (let w = 1; w <= this.np; w++) {
      const x = this.taken[v * (this.np + 1) + w];
      if (w !== v && x > most) {
        most = x;
        best = w;
      }
    }
    return best;
  }

  private sampleLand(state: MatchState): void {
    const n = this.np + 1;
    for (let i = 0; i < this.taken.length; i++) this.taken[i] *= LAND_DECAY;
    const prev = this.landSample;
    for (let h = 0; h < prev.length; h++) {
      const o = state.owner[h];
      const was = prev[h];
      if (o !== was) {
        if (was > 0 && o > 0) this.taken[was * n + o]++;
        prev[h] = o;
      }
    }
  }

  /** The score leader among living nations (ties: lower id), told once held LEAD_HOLD. */
  private lead(state: MatchState, t: Tick): void {
    if (t < LEAD_FROM) return;
    let id = 0;
    let best = -1;
    for (const p of state.players) {
      if (p.eliminatedTick < 0 && p.score > best) {
        best = p.score;
        id = p.id;
      }
    }
    if (!id || id === this.leader) {
      this.candidate = 0;
      return;
    }
    if (id !== this.candidate) {
      this.candidate = id;
      this.candidateSince = t;
      return;
    }
    if (t - this.candidateSince < LEAD_HOLD) return;
    this.push({ t: this.candidateSince, k: 'leader', a: id, b: this.leader, n: state.players[id - 1].score });
    this.leader = id;
    this.candidate = 0;
  }

  private dominationStep(state: MatchState, t: Tick): void {
    const d = state.domination;
    if (!d.holder || d.sinceTick === this.dominationTold || t - d.sinceTick < DOMINATION_WARN) return;
    this.dominationTold = d.sinceTick;
    const teams = state.settings.mode === 'teams';
    const members = state.players.filter((p) => p.eliminatedTick < 0 && (teams ? p.team === d.holder : p.id === d.holder));
    if (!members.length) return;
    const share = members.reduce((s, p) => s + p.areaShare, 0);
    this.push({ t: d.sinceTick, k: 'domination', a: members[0].id, b: 0, n: Math.round(share * 100), ...(teams ? { f: 1 } : {}) });
  }

  private finalMinute(state: MatchState, t: Tick): void {
    const alive = state.players.filter((p) => p.eliminatedTick < 0).sort((x, y) => y.score - x.score || x.id - y.id);
    if (alive.length < 2) return;
    this.push({ t, k: 'final', a: alive[0].id, b: alive[1].id, n: alive[0].score - alive[1].score });
  }
}
